import ListContainer from '@anm/components/ListContainer';
import { getDynamicBreakpoint } from '@anm/components/list/Masonry';
import { DragOverProvider } from '@anm/contexts/DragOverContext';
import isMaxMedia from '@anm/helpers/is/isMaxMedia';
import isMobileOrIPad from '@anm/helpers/is/isMobileOrIPad';
import { Features } from '@anm/shared/features';
import { desktopFirstSizes } from '@anm/styles/helpers';
import { FC } from 'react';
import { useSelector } from 'react-redux';

import { projectSelectors } from '../../../store/modules/project';
import OpenTwoOptionsDialog from '../../OpenTwoOptionsDialog';
import { useFetchBranding } from '../../hosting/CustomizationSection/tabs/LandingTab/hooks';
import { HeaderWrapper } from '../../layouts/LeftBarLayout/Wrapper';
import MyProjectsActions from '../MyProjectsActions';
import ProjectBreadcrumbs from '../ProjectBreadcrumbs';
import RemovedProjectError from '../RemovedProjectError';
import VideoFolders from '../VideoFolders';

import { Content, DropZone, MyVideoHeaderButtonsWrapper } from './Wrapper';
import { useDropZoneBG, useFocusField, useRemovedProjectError, useUploadFileByDragAndDrop } from './hooks';

const MyVideosSection: FC = () => {
  useFetchBranding();
  const isSearchActive = !!useSelector(projectSelectors.selectProjectsSearchKeyWord);

  const [canFocusField, setCanFocusField, handleCreateNewFolder] = useFocusField();
  const [
    dropZoneRef,
    isFolderDragOver,
    isDropZoneDragOver,
    handleDropFile,
    handleDragOver,
    handleDragLeave,
    setIsFolderDragOver,
    setIsDropZoneDragOver
  ] = useUploadFileByDragAndDrop();
  const [contentRef, DropZoneBGContent] = useDropZoneBG(isDropZoneDragOver);

  const canShowProjectError = useRemovedProjectError();

  const canShowDragZoneArea = !isFolderDragOver && isDropZoneDragOver && !isMobileOrIPad();

  return (
    <div onDragLeave={handleDragLeave}>
      {Features.isTwoOptionsPopupEnabled && <OpenTwoOptionsDialog />}
      {canShowProjectError ? (
        <RemovedProjectError />
      ) : (
        <DropZone
          id="project-section-drop-zone"
          ref={dropZoneRef}
          onDrop={handleDropFile}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {canShowDragZoneArea && <DropZoneBGContent />}
          <DragOverProvider
            value={{
              isDragOver: isFolderDragOver,
              isRootDragOver: isDropZoneDragOver,
              setIsDragOver: setIsFolderDragOver,
              setIsRootDragOver: setIsDropZoneDragOver
            }}
          >
            <HeaderWrapper>
              {!isSearchActive && <ProjectBreadcrumbs />}
              <MyVideoHeaderButtonsWrapper>
                <MyProjectsActions onCreateNewFolder={handleCreateNewFolder} />
              </MyVideoHeaderButtonsWrapper>
            </HeaderWrapper>
            <ListContainer itemPadding={40} itemWidth={isMobileOrIPad() ? 160 : 320}>
              {containerWidth => (
                <Content
                  id="videos-content"
                  ref={contentRef}
                  variant="full-size"
                  containerWidth={containerWidth}
                  scrollDirection
                >
                  <VideoFolders
                    {...{ canFocusField }}
                    onCanFocusField={setCanFocusField}
                    breakpointCols={() => ({
                      default: 5,
                      1959: 5,
                      1639: 4,
                      1319: 2,
                      800: 2,
                      767: 3,
                      ...getDynamicBreakpoint({
                        containerWidth: containerWidth + 40,
                        itemWidth: isMaxMedia(desktopFirstSizes.phone)
                          ? containerWidth / 2
                          : isMaxMedia(desktopFirstSizes.tablet)
                          ? (containerWidth - 40) / 3
                          : 320
                      })
                    })}
                  />
                </Content>
              )}
            </ListContainer>
          </DragOverProvider>
        </DropZone>
      )}
    </div>
  );
};

export default MyVideosSection;
